import type { ReactNode } from "react";
import { Moon, Sun } from "lucide-react";
import { BrandMark } from "../../shared/ui";
import { toggleTheme, useTheme } from "../../app/theme";

/**
 * An illustration of the one thing this product does differently: an allocation is a *dated*
 * record, so closing one does not erase it. Static and decorative — hence `aria-hidden` and the
 * "Example" label, which stops it reading as this workspace's real data.
 */
const LEDGER = [
  { who: "AR", project: "APOLLO", range: "1 Jan – 31 Mar", share: 60, state: "held" },
  { who: "MK", project: "APOLLO", range: "1 Jan – 31 Mar", share: 40, state: "closed" },
  { who: "MK", project: "ORBIT", range: "1 Apr – open", share: 40, state: "current" },
] as const;

/**
 * The frame for the four unauthenticated screens.
 *
 * <p>Two columns: the record panel on the left, the form on the right. Each column centres its own
 * content vertically, so neither ends up as a band of text stranded against an edge. The panel is
 * hidden below `lg`, where the form should have the whole screen rather than compete with it.
 *
 * <p>The theme toggle is present here as well as inside the application. Sign-in is the first
 * thing anyone sees, and someone who works in the dark should not have to authenticate through a
 * white page first.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Small print under the card — help routes, not marketing. */
  footer?: ReactNode;
}) {
  const theme = useTheme();

  return (
    <div className="flex min-h-screen bg-ink-50">
      {/* ---- Left: the record ---- */}
      <aside className="auth-panel relative hidden overflow-hidden lg:flex lg:w-[44%] lg:flex-col lg:px-12 lg:py-11 xl:px-14">
        <div className="auth-panel-grid pointer-events-none absolute inset-0" aria-hidden="true" />

        <div className="relative z-10 flex items-center gap-3">
          {/* No tile: the mark carries its own field, and the panel behind it is close enough in
              hue that it needs the ring to stay a distinct object. */}
          <BrandMark className="h-10 w-10 rounded-full ring-1 ring-white/20" />
          <span className="text-sm font-semibold tracking-tight text-white">TaZlo</span>
        </div>

        <div className="auth-rise relative z-10 flex flex-1 flex-col justify-center py-12">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-brand-200 dark:text-white/55">
            System of record
          </p>
          <h2 className="font-display mt-4 max-w-[19ch] text-[2.1rem] font-semibold leading-[1.12] tracking-tight text-white xl:text-[2.4rem]">
            Plan the work. Staff it honestly. Keep the record straight.
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
            Membership and allocation are held against the dates they actually applied, so a report
            about January still says January.
          </p>

          {/* The signature: three rows of the thing itself. */}
          <div
            className="mt-9 max-w-md rounded-xl bg-white/[0.06] p-4 ring-1 ring-inset ring-white/10 backdrop-blur-sm"
            aria-hidden="true"
          >
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/70">
                Allocation ledger
              </span>
              <span className="text-[10px] uppercase tracking-[0.14em] text-white/40">Example</span>
            </div>

            <ul className="mt-3.5 space-y-2.5">
              {LEDGER.map((row) => (
                <li
                  key={`${row.who}-${row.project}-${row.range}`}
                  className={row.state === "closed" ? "opacity-45" : undefined}
                >
                  <div className="flex items-center gap-3">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-white/10 text-[10px] font-semibold tracking-wide text-white/90">
                      {row.who}
                    </span>
                    <span className="w-16 shrink-0 text-[11px] font-semibold tracking-wide text-white">
                      {row.project}
                    </span>
                    <span
                      className={`tnum w-[7.5rem] shrink-0 text-[11px] text-white/70 ${
                        row.state === "closed" ? "line-through decoration-white/40" : ""
                      }`}
                    >
                      {row.range}
                    </span>
                    <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                      <span
                        className={`block h-full rounded-full ${
                          row.state === "current" ? "bg-emerald-400" : "bg-white/55"
                        }`}
                        style={{ width: `${row.share}%` }}
                      />
                    </span>
                    <span className="tnum w-8 shrink-0 text-right text-[11px] text-white/70">
                      {row.share}%
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-4 border-t border-white/10 pt-3 text-[11px] leading-relaxed text-white/50">
              The closed row stays on the record. Ask for March and March is what you get.
            </p>
          </div>
        </div>

        <p className="relative z-10 text-xs text-white/45">Huewine · internal use only</p>
      </aside>

      {/* ---- Right: the form ---- */}
      <main className="relative flex flex-1 items-center justify-center px-5 py-12">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          {theme === "dark" ? (
            <Sun className="h-[18px] w-[18px]" />
          ) : (
            <Moon className="h-[18px] w-[18px]" />
          )}
        </button>

        <div className="auth-rise w-full max-w-[25rem]">
          <div className="mb-7 flex items-center gap-3 lg:hidden">
            <BrandMark className="h-9 w-9 rounded-full" />
            <span className="text-sm font-semibold tracking-tight text-ink-900">
              TaZlo
            </span>
          </div>

          <div className="auth-card rounded-2xl border border-ink-200 bg-surface p-7 sm:p-8">
            <h1 className="font-display text-[1.6rem] font-semibold leading-tight tracking-tight text-ink-900">
              {title}
            </h1>
            {subtitle && <p className="mt-2 text-sm leading-relaxed text-ink-500">{subtitle}</p>}

            <div className="mt-7">{children}</div>
          </div>

          {footer && (
            <p className="mt-5 text-center text-xs leading-relaxed text-ink-500">{footer}</p>
          )}
        </div>
      </main>
    </div>
  );
}
